import React, { forwardRef, Ref } from 'react';
import cx from 'classnames';

import { PropsOf, AnyElement } from '@/utils/types';
import { isArray } from '@/utils';
import { columnClass } from './columnClass';

type ResponsiveProp<PropValue> =
  | PropValue
  | [PropValue, PropValue]
  | [PropValue, PropValue, PropValue];

type NumberColumns = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type ResponsiveColumn = ResponsiveProp<NumberColumns>;

export type ColumnProp<T extends AnyElement> = Omit<PropsOf<T>, 'children'> & {
  as?: T;
  size?: ResponsiveColumn;
  children?: React.ReactNode;
  className?: string;
};

const createColumnClass = (size: [number, number, number]): string => {
  const [sm, md, lg] = size.map((s) => s - 1);
  const { large, medium, small } = columnClass;
  return `${large[lg]} ${medium[md]} ${small[sm]}`;
};

const normalizeResponsiveProp = (size?: ResponsiveColumn): string => {
  if (!size) {
    return 'flex-1';
  }

  if (isArray(size)) {
    const [smallSize, mediumSize] = size;

    switch (size.length) {
      case 3:
        return createColumnClass(size);
      case 2:
        return createColumnClass([smallSize, mediumSize, mediumSize]);
      default:
        return createColumnClass([smallSize, smallSize, smallSize]);
    }
  }
  return createColumnClass([size, size, size]);
};

const Column = <T extends AnyElement>(
  { as: Tag, size, children, className, ...rest }: ColumnProp<T>,
  ref: Ref<T>,
) => {
  const ColumnTag = (Tag || 'div') as React.ElementType;
  const classNames = cx(
    'max-w-full px-2',
    normalizeResponsiveProp(size),
    className,
  );

  return (
    <ColumnTag ref={ref} className={classNames} {...rest}>
      {children}
    </ColumnTag>
  );
};

export default forwardRef(Column);
